import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, BookOpen } from "lucide-react";
import { useApiMutation } from "@/hooks/useApiMutation";
import { showError, showSuccess } from "@/lib/toastHelpers";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChefAiqRecipe {
  _id: string;
  name: string;
  description?: string;
  category?: string;
  cuisine?: string;
  mealType?: string;
  difficulty?: string;
  prepTime?: number;
  cookTime?: number;
  servings?: number;
  status?: string;
  visibility?: string;
  dietType?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface RecipeFormData {
  name: string;
  description: string;
  category: string;
  cuisine: string;
  mealType: string;
  difficulty: string;
  prepTime: string;
  cookTime: string;
  servings: string;
  status: string;
}

const EMPTY_FORM: RecipeFormData = {
  name: "",
  description: "",
  category: "",
  cuisine: "",
  mealType: "",
  difficulty: "",
  prepTime: "",
  cookTime: "",
  servings: "",
  status: "draft",
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function ChefAiqRecipes() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<ChefAiqRecipe | null>(null);
  const [deletingRecipe, setDeletingRecipe] = useState<ChefAiqRecipe | null>(null);
  const [form, setForm] = useState<RecipeFormData>(EMPTY_FORM);

  const { data, isLoading } = useQuery<ChefAiqRecipe[]>({
    queryKey: ["/api/chefaiq-recipes"],
    queryFn: () => apiRequest("GET", "/api/chefaiq-recipes") as unknown as Promise<ChefAiqRecipe[]>,
  });

  const recipes = (data ?? []).filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.category?.toLowerCase().includes(search.toLowerCase()) ||
    r.cuisine?.toLowerCase().includes(search.toLowerCase())
  );

  const createMutation = useApiMutation({
    endpoint: "/api/chefaiq-recipes",
    method: "POST",
    invalidate: [["/api/chefaiq-recipes"]],
    successMessage: "Recipe created successfully.",
    onSuccess: () => { setFormOpen(false); setForm(EMPTY_FORM); },
  });

  const updateMutation = useApiMutation({
    endpoint: `/api/chefaiq-recipes/${editingRecipe?._id}`,
    method: "PATCH",
    invalidate: [["/api/chefaiq-recipes"]],
    successMessage: "Recipe updated successfully.",
    onSuccess: () => { setFormOpen(false); setEditingRecipe(null); setForm(EMPTY_FORM); },
  });

  const handleOpenCreate = () => {
    setEditingRecipe(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const handleOpenEdit = (recipe: ChefAiqRecipe) => {
    setEditingRecipe(recipe);
    setForm({
      name: recipe.name,
      description: recipe.description ?? "",
      category: recipe.category ?? "",
      cuisine: recipe.cuisine ?? "",
      mealType: recipe.mealType ?? "",
      difficulty: recipe.difficulty ?? "",
      prepTime: recipe.prepTime != null ? String(recipe.prepTime) : "",
      cookTime: recipe.cookTime != null ? String(recipe.cookTime) : "",
      servings: recipe.servings != null ? String(recipe.servings) : "",
      status: recipe.status ?? "draft",
    });
    setFormOpen(true);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) { showError("Recipe name is required."); return; }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      category: form.category.trim() || undefined,
      cuisine: form.cuisine.trim() || undefined,
      mealType: form.mealType || undefined,
      difficulty: form.difficulty || undefined,
      prepTime: form.prepTime ? Number(form.prepTime) : undefined,
      cookTime: form.cookTime ? Number(form.cookTime) : undefined,
      servings: form.servings ? Number(form.servings) : undefined,
      status: form.status,
    };
    if (editingRecipe) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = async () => {
    if (!deletingRecipe) return;
    try {
      await apiRequest("DELETE", `/api/chefaiq-recipes/${deletingRecipe._id}`);
      qc.invalidateQueries({ queryKey: ["/api/chefaiq-recipes"] });
      showSuccess("Recipe deleted.");
    } catch {
      showError("Failed to delete recipe.");
    } finally {
      setDeletingRecipe(null);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen className="h-7 w-7" />
            ChefAiQ Library Recipes
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Free template recipes shown to all ChefAiQ users at /library/chefaiq-library
          </p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 shrink-0">
          <Plus className="h-4 w-4" />
          Add Recipe
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search recipes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : recipes.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">
              {search ? "No recipes match your search." : "No ChefAiQ library recipes yet. Click \"Add Recipe\" to create the first one."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden md:table-cell">Category</TableHead>
                  <TableHead className="hidden lg:table-cell">Cuisine</TableHead>
                  <TableHead className="hidden lg:table-cell">Difficulty</TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  <TableHead className="w-[90px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipes.map((recipe) => (
                  <TableRow key={recipe._id}>
                    <TableCell>
                      <div className="font-medium">{recipe.name}</div>
                      {recipe.description && (
                        <div className="text-xs text-muted-foreground truncate max-w-[280px]">
                          {recipe.description}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                      {recipe.category || "—"}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {recipe.cuisine || "—"}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {recipe.difficulty || "—"}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant={recipe.status === "published" ? "default" : "secondary"}>
                        {recipe.status ?? "draft"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenEdit(recipe)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeletingRecipe(recipe)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={(open) => { if (!open) { setFormOpen(false); setEditingRecipe(null); setForm(EMPTY_FORM); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRecipe ? "Edit Recipe" : "Add ChefAiQ Recipe"}</DialogTitle>
            <DialogDescription>
              {editingRecipe
                ? "Update the recipe details. Ingredients and procedures can be managed in the main app."
                : "Create a new free template recipe for the ChefAiQ library."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Name */}
            <div className="space-y-1.5">
              <Label>Name <span className="text-destructive">*</span></Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Classic Chocolate Cake"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of the recipe…"
                rows={3}
              />
            </div>

            {/* Category + Cuisine */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  placeholder="e.g. Desserts"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Cuisine</Label>
                <Input
                  value={form.cuisine}
                  onChange={(e) => setForm((f) => ({ ...f, cuisine: e.target.value }))}
                  placeholder="e.g. French"
                />
              </div>
            </div>

            {/* Meal Type + Difficulty */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Meal Type</Label>
                <Select
                  value={form.mealType}
                  onValueChange={(v) => setForm((f) => ({ ...f, mealType: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breakfast">Breakfast</SelectItem>
                    <SelectItem value="lunch">Lunch</SelectItem>
                    <SelectItem value="dinner">Dinner</SelectItem>
                    <SelectItem value="snack">Snack</SelectItem>
                    <SelectItem value="dessert">Dessert</SelectItem>
                    <SelectItem value="appetizer">Appetizer</SelectItem>
                    <SelectItem value="beverage">Beverage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Difficulty</Label>
                <Select
                  value={form.difficulty}
                  onValueChange={(v) => setForm((f) => ({ ...f, difficulty: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Easy">Easy</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Prep / Cook / Servings */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Prep (min)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.prepTime}
                  onChange={(e) => setForm((f) => ({ ...f, prepTime: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Cook (min)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.cookTime}
                  onChange={(e) => setForm((f) => ({ ...f, cookTime: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Servings</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.servings}
                  onChange={(e) => setForm((f) => ({ ...f, servings: e.target.value }))}
                  placeholder="4"
                />
              </div>
            </div>

            {/* Status */}
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setFormOpen(false); setEditingRecipe(null); setForm(EMPTY_FORM); }}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? "Saving…" : editingRecipe ? "Update Recipe" : "Create Recipe"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingRecipe} onOpenChange={(open) => { if (!open) setDeletingRecipe(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recipe</AlertDialogTitle>
            <AlertDialogDescription>
              Delete <strong>{deletingRecipe?.name}</strong> from the ChefAiQ library? This cannot be undone. Users who already copied this recipe will keep their copy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
